/**
 * DDDs realmente atribuídos pela Anatel. Sem a lista, "(55) 24998-6754" era
 * aceito no cadastro e o código de WhatsApp saía para lugar nenhum.
 *
 * Espelha `normalizeBrazilPhoneForEvolution` na API — as duas pontas precisam
 * recusar o mesmo número, senão o app deixa passar e a API é quem reclama.
 */
const BRAZIL_AREA_CODES = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43,
  44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77,
  79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

/** Dígitos nacionais (DDD + local), já sem o DDI 55. */
function nationalDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  // Abaixo de 12 dígitos o "55" da frente é DDD (Santa Maria/RS), não país.
  return digits.startsWith('55') && digits.length >= 12 && digits.length <= 13
    ? digits.slice(2)
    : digits;
}

/**
 * Mensagem de erro do telefone, ou `null` quando ele serve para WhatsApp.
 * Só celular: fixo não recebe mensagem, então recusar aqui evita um cadastro
 * que nunca receberia o código.
 */
export function getBrazilPhoneError(raw: string): string | null {
  const national = nationalDigits(raw);

  if (national.length < 10) return 'Informe o WhatsApp com DDD, como (11) 99999-9999.';
  if (national.length > 11) return 'Número muito longo. Informe DDD + celular.';

  if (!BRAZIL_AREA_CODES.has(Number(national.slice(0, 2)))) {
    return `DDD ${national.slice(0, 2)} não existe. Confira os dois primeiros dígitos.`;
  }

  const local = national.slice(2);
  const isMobile = local.length === 9 ? local.startsWith('9') : /^[6-9]/.test(local);
  if (!isMobile) return 'Informe um celular com WhatsApp — número fixo não recebe o código.';

  return null;
}

export function isValidBrazilPhone(raw: string): boolean {
  return getBrazilPhoneError(raw) === null;
}

/** (11) 99999-0000 — para mostrar de volta o número que a pessoa digitou. */
export function formatBrazilPhone(raw: string): string {
  const national = nationalDigits(raw);
  if (national.length < 10 || national.length > 11) return raw;

  const ddd = national.slice(0, 2);
  const local = national.slice(2);
  const split = local.length - 4;
  return `(${ddd}) ${local.slice(0, split)}-${local.slice(split)}`;
}

const DIGIT = /\d/;

/** (11) 99999-9999 */
const NATIONAL_PHONE_MASK: (string | RegExp)[] = [
  '(', DIGIT, DIGIT, ')', ' ',
  DIGIT, DIGIT, DIGIT, DIGIT, DIGIT, '-', DIGIT, DIGIT, DIGIT, DIGIT,
];

/** +55 (11) 99999-9999 */
const DDI_PHONE_MASK: (string | RegExp)[] = ['+', '5', '5', ' ', ...NATIONAL_PHONE_MASK];

/**
 * Máscara de telefone que aguenta o DDI.
 *
 * O autofill do navegador devolve "+55 24 99867-5401"; a máscara nacional
 * (11 posições) engolia o DDI como se fosse DDD e cortava os dois últimos
 * dígitos, virando um número que não existe. Passando de 11 dígitos com 55 na
 * frente, a máscara cresce e o DDI fica de fora do valor.
 */
export function brazilPhoneMask(text = ''): (string | RegExp)[] {
  const digits = text.replace(/\D/g, '');
  return digits.startsWith('55') && digits.length >= 12 ? DDI_PHONE_MASK : NATIONAL_PHONE_MASK;
}
